import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Row,
  Col,
  Card,
  Tag,
  Popconfirm,
  message,
  Tooltip,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FilePdfOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { Proposal, ProposalStatus } from '@/types'
import { generateProposalHTML } from '@/utils/proposalPdfGenerator'
import { api } from '@/services/api'
import { getTemplateBody, getCoverPage, getCompanyContent, getTemplateTermsHtml } from '@/utils/proposalTemplateBody'
import { useMasterData } from '@/context/MasterDataContext'
import { useModulePermission } from '@/hooks/useModulePermission'
import { proposalStore, type ProposalRow } from './proposalStore'
import { leadStore } from '../Lead/leadStore'
import { authStore } from '@/context/auth'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import './ProposalList.css'

const ProposalList: React.FC = () => {
  const navigate = useNavigate()
  const { proposalTemplates } = useMasterData()
  const { canDownload } = useModulePermission('Proposals')
  const signedIn = authStore.getUser()
  const isAdmin = signedIn?.role === 'admin'
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('')

  // Resolve which template to use: the one chosen on the proposal,
  // else the default template, else the first active one.
  const resolveTemplate = (templateName?: string) => {
    const active = proposalTemplates.filter(t => t.status === 'active')
    return (
      (templateName && active.find(t => t.name === templateName)) ||
      active.find(t => t.isDefault) ||
      active[0]
    )
  }

  // Newest-created first. The backend returns rows in no guaranteed order, so
  // this is enforced here rather than relying on insertion order surviving a
  // refresh. createdAt is date-only (YYYY-MM-DD) — same-day ties fall back to
  // id, which embeds a base36 creation timestamp and so still sorts recent-first.
  const sortNewestFirst = (list: ProposalRow[]) =>
    [...list].sort((a, b) =>
      (b.createdAt || '').localeCompare(a.createdAt || '') || (b.id || '').localeCompare(a.id || '')
    )

  const [proposals, setProposals] = useState<ProposalRow[]>(() => sortNewestFirst(proposalStore.getAll()))
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  // Stay in sync with the store: hydrate from the DB on mount and update live
  // whenever a proposal is added / edited / approved / rejected anywhere.
  useEffect(() => {
    const update = () => setProposals(sortNewestFirst(proposalStore.getAll()))
    update()
    proposalStore.refresh().then(update)
    window.addEventListener(proposalStore.eventName, update)
    // Also re-render when leads finish loading/changing, since Mode of Supply
    // and Type of Power are looked up from the lead at render time.
    window.addEventListener(leadStore.eventName, update)
    return () => {
      window.removeEventListener(proposalStore.eventName, update)
      window.removeEventListener(leadStore.eventName, update)
    }
  }, [])

  // Approve / Reject straight from the list — persists to the store/API.
  const handleApprove = (id: string) => {
    proposalStore.save({ id, status: ProposalStatus.APPROVED })
    setProposals(proposalStore.getAll())
    message.success('Proposal approved')
  }
  const handleReject = (id: string) => {
    proposalStore.save({ id, status: ProposalStatus.REJECTED })
    setProposals(proposalStore.getAll())
    message.info('Proposal rejected')
  }

  const statusColors: Record<ProposalStatus, string> = {
    [ProposalStatus.DRAFT]: 'default',
    [ProposalStatus.SUBMITTED]: 'blue',
    [ProposalStatus.UNDER_REVIEW]: 'orange',
    [ProposalStatus.APPROVED]: 'green',
    [ProposalStatus.REJECTED]: 'red',
    [ProposalStatus.EXPIRED]: 'default',
    [ProposalStatus.WON]: 'cyan',
    [ProposalStatus.LOST]: 'magenta',
  }

  const filteredProposals = proposals.filter((p) => {
    const matchSearch = p.id.toLowerCase().includes(searchText.toLowerCase()) ||
                        p.leadId.toLowerCase().includes(searchText.toLowerCase())
    const matchStatus = !filterStatus || p.status === filterStatus
    return matchSearch && matchStatus
  })

  // Calculate KPIs
  const kpiData = {
    total: proposals.length,
    pending: proposals.filter(p => 
      p.status === ProposalStatus.DRAFT || 
      p.status === ProposalStatus.SUBMITTED || 
      p.status === ProposalStatus.UNDER_REVIEW
    ).length,
    approved: proposals.filter(p => p.status === ProposalStatus.APPROVED).length,
  }

  const columns = [
    {
      title: 'Proposal Date',
      dataIndex: 'offerDate',
      key: 'offerDate',
      width: 130,
      render: (date: string, record: ProposalRow) => new Date(date || record.createdAt).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      }),
    },
    {
      title: 'Lead Name',
      dataIndex: 'leadName',
      key: 'leadName',
      width: 160,
      render: (text: string, record: ProposalRow) => (
        <span
          style={{ fontWeight: 700, color: '#1677ff', cursor: 'pointer' }}
          onClick={() => navigate(`/proposals/${record.id}`)}
        >
          {text || record.leadId}
        </span>
      ),
    },
    {
      title: 'Mode of Supply',
      key: 'modeOfSupply',
      width: 160,
      responsive: ['md'] as any[], // Hide on small screens for responsiveness
      render: (_: any, record: ProposalRow) => leadStore.getLead(record.leadId)?.modeOfSupply || '—',
    },
    {
      title: 'Type of Power',
      key: 'typeOfPower',
      width: 150,
      responsive: ['md'] as any[], // Hide on small screens for responsiveness
      render: (_: any, record: ProposalRow) => leadStore.getLead(record.leadId)?.typeOfPower || '—',
    },
    {
      title: 'Sale Power Price',
      dataIndex: 'pricingDetails',
      key: 'pricingDetails',
      width: 140,
      render: (pricingDetails: any, record: ProposalRow) => {
        const val = pricingDetails?.[0]?.rate || record.sakthiTariff || record.tariff
        return <strong style={{ color: '#0f172a' }}>₹{Number(val || 0).toFixed(2)} / kWh</strong>
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: ProposalStatus) => (
        <Tag color={statusColors[status]} style={{ padding: '2px 8px', borderRadius: '4px', fontWeight: 500 }}>
          {status === ProposalStatus.UNDER_REVIEW && <ClockCircleOutlined style={{ marginRight: 4 }} />}
          {status === ProposalStatus.APPROVED && <CheckCircleOutlined style={{ marginRight: 4 }} />}
          {status === ProposalStatus.DRAFT && <FileTextOutlined style={{ marginRight: 4 }} />}
          {status === ProposalStatus.WON && <CheckCircleOutlined style={{ marginRight: 4 }} />}
          {status === ProposalStatus.LOST && <CloseCircleOutlined style={{ marginRight: 4 }} />}
          {status}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 110,
      responsive: ['md'] as any[], // Hide on small screens for responsiveness
      render: (date: string) => new Date(date).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      }),
    },
    {
      title: 'Expires',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 120,
      render: (date: string) => {
        const diffTime = new Date(date).getTime() - new Date().getTime()
        const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        
        if (daysLeft < 0) {
          return <Tag color="red">Expired</Tag>
        } else if (daysLeft < 5) {
          return (
            <Tooltip title={`${daysLeft} days remaining`}>
              <span style={{ color: '#ef4444', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <ExclamationCircleOutlined /> {daysLeft}d left
              </span>
            </Tooltip>
          )
        } else {
          return (
            <Tooltip title={`${daysLeft} days remaining`}>
              <span style={{ color: '#10b981', fontWeight: 600 }}>
                {daysLeft}d left
              </span>
            </Tooltip>
          )
        }
      },
    },
    {
      title: 'Actions',
      key: 'action',
      width: 230,
      fixed: 'right' as const,
      render: (_: any, record: ProposalRow) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              className="pl-action-btn"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/proposals/${record.id}`)}
            />
          </Tooltip>
          {/* Allowlist of pre-approval statuses, not a denylist — the
              previous check only excluded Approved/Rejected, so Won/Lost/
              Expired rows still showed these buttons too. */}
          {(record.status === ProposalStatus.DRAFT ||
            record.status === ProposalStatus.SUBMITTED ||
            record.status === ProposalStatus.UNDER_REVIEW) && (
              <>
                <Popconfirm
                  title="Approve Proposal"
                  description="Mark this proposal as approved?"
                  okText="Approve"
                  cancelText="Cancel"
                  onConfirm={() => handleApprove(record.id)}
                >
                  <Tooltip title="Approve">
                    <Button
                      className="pl-action-btn"
                      icon={<CheckCircleOutlined />}
                      style={{ color: '#059669', borderColor: '#059669' }}
                    />
                  </Tooltip>
                </Popconfirm>
                <Popconfirm
                  title="Reject Proposal"
                  description="Mark this proposal as rejected?"
                  okText="Reject"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => handleReject(record.id)}
                >
                  <Tooltip title="Reject">
                    <Button className="pl-action-btn" icon={<CloseCircleOutlined />} danger />
                  </Tooltip>
                </Popconfirm>
              </>
            )}
          {canDownload && (
            <Tooltip title="Download PDF">
              <Button
                className="pl-action-btn"
                icon={<FilePdfOutlined />}
                loading={downloadingId === record.id}
                onClick={async () => {
                  setDownloadingId(record.id)
                  try {
                    const tpl = resolveTemplate(record.template)
                    // Customer block from the proposal's lead — same lookup
                    // ProposalDetail.tsx uses, this download button just never
                    // had it wired in.
                    const lead = leadStore.getLead(record.leadId)
                    // Rendered server-side via Puppeteer — html2pdf.js/
                    // html2canvas couldn't reliably capture the cover page's
                    // background image (came back blank).
                    const html = generateProposalHTML({
                      proposal: record as unknown as Proposal,
                      customerName: record.leadName || record.leadId,
                      customerAddress: lead?.address || '—',
                      customerCity: [lead?.city, lead?.pinCode].filter(Boolean).join(' - ') || lead?.city || '—',
                      templateBodyHtml: getTemplateBody(tpl),
                      coverPage: getCoverPage(tpl),
                      companyContentHtml: getCompanyContent(tpl),
                      termsConditionsHtml: getTemplateTermsHtml(tpl),
                    })
                    await api.downloadProposalPdf(record.id, html, `Proposal_${record.referenceNumber || record.id}.pdf`)
                    message.success('Proposal PDF downloaded successfully!')
                  } catch (err) {
                    console.error('Proposal PDF download failed:', err)
                    message.error('Failed to generate PDF download. Please try again.')
                  } finally {
                    setDownloadingId(null)
                  }
                }}
              />
            </Tooltip>
          )}
          {/* Edit / Negotiate button: Won is a closed deal — never editable,
              not even by Admin (see crudService.update()'s matching backend
              guard). APPROVED can still be negotiated, Admin only. */}
          {record.status !== ProposalStatus.WON &&
          (record.status !== ProposalStatus.APPROVED || isAdmin) ? (
            <Tooltip title={record.status === ProposalStatus.APPROVED ? "Negotiate (Admin Only)" : "Edit"}>
              <Button
                className="pl-action-btn"
                icon={<EditOutlined />}
                onClick={() => navigate(`/proposals/${record.id}/edit`)}
                style={record.status === ProposalStatus.APPROVED ? { color: '#001A4D', borderColor: '#001A4D' } : {}}
              />
            </Tooltip>
          ) : null}
          {record.status === ProposalStatus.DRAFT && (
            <Popconfirm
              title="Delete Proposal"
              description="Are you sure you want to delete this proposal?"
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              onConfirm={() => {
                proposalStore.remove(record.id)
                setProposals(proposalStore.getAll())
                message.success('Proposal deleted successfully')
              }}
            >
              <Tooltip title="Delete">
                <Button className="pl-action-btn pl-action-btn-danger" icon={<DeleteOutlined />} danger />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  if (!proposalStore.loaded || !leadStore.loaded) return <TableSkeleton columns={9} />

  return (
    <div className="proposal-list-container">
      {/* Header */}
      <div className="pl-header">
        <div>
          <h2>
            <FilePdfOutlined className="pl-header-icon" />
            Proposal Management
          </h2>
          <p className="pl-header-sub">
            Generate pricing details, configure tax details, and manage approvals workflow.
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => navigate('/proposals/new')}
          style={{ height: '42px', fontWeight: 600 }}
        >
          Create Proposal
        </Button>
      </div>

      {/* KPI Dashboard Cards */}
      <KpiCardRow
        cards={[
          {
            label: 'Total Proposals',
            value: kpiData.total,
            icon: <FileTextOutlined style={{ fontSize: 24, color: '#2563eb' }} />,
            iconBg: 'rgba(37,99,235,0.08)',
            borderColor: '#2563eb',
          },
          {
            label: 'Pending Review',
            value: kpiData.pending,
            icon: <ClockCircleOutlined style={{ fontSize: 24, color: '#d97706' }} />,
            iconBg: 'rgba(217,119,6,0.08)',
            borderColor: '#d97706',
          },
          {
            label: 'Approved',
            value: kpiData.approved,
            icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#10b981' }} />,
            iconBg: 'rgba(16,185,129,0.08)',
            borderColor: '#10b981',
          },
        ]}
      />

      {/* Filters Search */}
      <Card className="pl-filter-card" style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={14} lg={12}>
            <Input.Search
              placeholder="Search by Proposal ID or Lead ID..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size="large"
            />
          </Col>
          <Col xs={24} md={10} lg={6}>
            <Select
              placeholder="Filter by Status"
              allowClear
              value={filterStatus || undefined}
              onChange={setFilterStatus}
              style={{ width: '100%' }}
              size="large"
              options={[
                { label: 'Draft', value: ProposalStatus.DRAFT },
                { label: 'Under Review', value: ProposalStatus.UNDER_REVIEW },
                { label: 'Approved', value: ProposalStatus.APPROVED },
                { label: 'Rejected', value: ProposalStatus.REJECTED },
                { label: 'Won', value: ProposalStatus.WON },
                { label: 'Lost', value: ProposalStatus.LOST },
              ]}
            />
          </Col>
        </Row>
      </Card>

      {/* Table Results */}
      <Card className="pl-table-card">
        <Table
          dataSource={filteredProposals}
          columns={columns}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} proposals`
          }}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  )
}

export default ProposalList
